import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { listSkills, SkillInfo } from '../lib/api';
import { downloadJson, assemblyLineFileName } from '../lib/assemblyLineIo';
import { Guardrail } from '../types';
import { GuardrailsEditor } from '../components/GuardrailsEditor';
import { I, Station } from '../components/Shared';
import { Shell } from '../components/Shell';

interface PhaseForm {
  key: string;
  label: string;
  desc: string;
  /** True once the user has hand-edited the key, so it stops tracking the label. */
  keyEdited: boolean;
  /** Mandatory phase — can't be skipped from the Landing Advanced-settings picker. */
  required: boolean;
  steps: StepForm[];
}

interface StepForm {
  key: string;
  label: string;
  desc: string;
  skillRef: string;
  dependsOn: string[];
  guardrails: Guardrail[];
  confluenceStepSync: boolean;
  gitStepSync: boolean;
  /** Mandatory step — can't be skipped from the Landing Advanced-settings picker. */
  required: boolean;
  keyEdited: boolean;
}

/** Derive a URL-safe key from a human label (mirrors the server slugify). */
function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function AssemblyLineEditor() {
  const { assemblyLineId } = useParams<{ assemblyLineId: string }>();
  const navigate = useNavigate();
  const editingAssemblyLine = useStore((s) => s.editingAssemblyLine);
  const loadAssemblyLineForEdit = useStore((s) => s.loadAssemblyLineForEdit);
  const saveAssemblyLineToServer = useStore((s) => s.saveAssemblyLineToServer);
  const updateAssemblyLineOnServer = useStore((s) => s.updateAssemblyLineOnServer);
  const importedDraft = useStore((s) => s.importedDraft);
  const clearImportedDraft = useStore((s) => s.clearImportedDraft);

  // Load assemblyLine from URL param if not already loaded
  useEffect(() => {
    if (assemblyLineId && (!editingAssemblyLine || editingAssemblyLine.id !== assemblyLineId)) {
      void loadAssemblyLineForEdit(assemblyLineId);
    }
  }, [assemblyLineId, editingAssemblyLine, loadAssemblyLineForEdit]);

  const isNew = !assemblyLineId || !editingAssemblyLine;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // Branding / platform metadata is no longer edited here, but is preserved
  // across a save so existing assemblyLines don't lose it.
  const [brandLabel, setBrandLabel] = useState('');
  const [brandHeadline, setBrandHeadline] = useState('');
  const [brandSubheadline, setBrandSubheadline] = useState('');
  const [metadataTabKey, setMetadataTabKey] = useState('');
  const [platformOptions, setPlatformOptions] = useState('');
  const [phases, setPhases] = useState<PhaseForm[]>([]);
  const [skills, setSkills] = useState<SkillInfo[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listSkills().then(setSkills).catch(() => setSkills([]));
  }, []);

  useEffect(() => {
    if (editingAssemblyLine) {
      setName(editingAssemblyLine.name);
      setDescription(editingAssemblyLine.description);
      setBrandLabel(editingAssemblyLine.brandLabel ?? '');
      setBrandHeadline(editingAssemblyLine.brandHeadline ?? '');
      setBrandSubheadline(editingAssemblyLine.brandSubheadline ?? '');
      setMetadataTabKey(editingAssemblyLine.metadataTabKey ?? '');
      setPlatformOptions(editingAssemblyLine.platformOptions?.join(', ') ?? '');
      setPhases(
        editingAssemblyLine.phases.map((p) => ({
          key: p.key,
          label: p.label,
          desc: p.desc,
          keyEdited: true,
          required: p.required === true,
          steps: p.steps.map((s) => ({
            key: s.key,
            label: s.label,
            desc: s.desc,
            skillRef: s.skillRef,
            dependsOn: s.dependsOn ?? [],
            guardrails: s.guardrails ?? [],
            confluenceStepSync: s.confluenceStepSync !== false,
            gitStepSync: s.gitStepSync !== false,
            required: s.required === true,
            keyEdited: true,
          })),
        })),
      );
    }
  }, [editingAssemblyLine]);

  // Pre-fill from an imported JSON file. Only applies to a *new* assemblyLine
  // (no :assemblyLineId in the route); the draft is consumed once so navigating
  // away and back doesn't silently resurrect it.
  useEffect(() => {
    if (assemblyLineId || !importedDraft) return;
    setName(importedDraft.name ?? '');
    setDescription(importedDraft.description ?? '');
    setBrandLabel(importedDraft.brandLabel ?? '');
    setBrandHeadline(importedDraft.brandHeadline ?? '');
    setBrandSubheadline(importedDraft.brandSubheadline ?? '');
    setMetadataTabKey(importedDraft.metadataTabKey ?? '');
    setPlatformOptions(importedDraft.platformOptions?.join(', ') ?? '');
    setPhases(
      (importedDraft.phases ?? []).map((p) => ({
        key: p.key,
        label: p.label,
        desc: p.desc,
        keyEdited: true,
        required: p.required === true,
        steps: (p.steps ?? []).map((s) => ({
          key: s.key,
          label: s.label,
          desc: s.desc,
          skillRef: s.skillRef,
          dependsOn: s.dependsOn ?? [],
          guardrails: s.guardrails ?? [],
          confluenceStepSync: s.confluenceStepSync !== false,
          gitStepSync: s.gitStepSync !== false,
          required: s.required === true,
          keyEdited: true,
        })),
      })),
    );
    clearImportedDraft();
  }, [assemblyLineId, importedDraft, clearImportedDraft]);

  const addPhase = (): void => {
    setPhases([...phases, { key: '', label: '', desc: '', keyEdited: false, required: false, steps: [] }]);
  };

  const removePhase = (idx: number): void => {
    setPhases(phases.filter((_, i) => i !== idx));
  };

  const updatePhaseDesc = (idx: number, value: string): void => {
    setPhases(phases.map((p, i) => (i === idx ? { ...p, desc: value } : p)));
  };

  const togglePhaseRequired = (idx: number): void => {
    setPhases(phases.map((p, i) => (i === idx ? { ...p, required: !p.required } : p)));
  };

  /** Label drives the key until the key has been hand-edited. */
  const setPhaseLabel = (idx: number, label: string): void => {
    setPhases(
      phases.map((p, i) =>
        i === idx ? { ...p, label, key: p.keyEdited ? p.key : slugify(label) } : p,
      ),
    );
  };

  const setPhaseKey = (idx: number, key: string): void => {
    setPhases(phases.map((p, i) => (i === idx ? { ...p, key, keyEdited: true } : p)));
  };

  const addStep = (phaseIdx: number): void => {
    setPhases(
      phases.map((p, i) =>
        i === phaseIdx
          ? {
              ...p,
              steps: [
                ...p.steps,
                { key: '', label: '', desc: '', skillRef: '', dependsOn: [], guardrails: [], confluenceStepSync: true, gitStepSync: true, required: false, keyEdited: false },
              ],
            }
          : p,
      ),
    );
  };

  const removeStep = (phaseIdx: number, stepIdx: number): void => {
    setPhases(
      phases.map((p, i) =>
        i === phaseIdx
          ? { ...p, steps: p.steps.filter((_, si) => si !== stepIdx) }
          : p,
      ),
    );
  };

  const patchStep = (phaseIdx: number, stepIdx: number, patch: Partial<StepForm>): void => {
    setPhases(
      phases.map((p, i) =>
        i === phaseIdx
          ? {
              ...p,
              steps: p.steps.map((s, si) => (si === stepIdx ? { ...s, ...patch } : s)),
            }
          : p,
      ),
    );
  };

  const setStepLabel = (phaseIdx: number, stepIdx: number, label: string): void => {
    setPhases(
      phases.map((p, i) =>
        i === phaseIdx
          ? {
              ...p,
              steps: p.steps.map((s, si) =>
                si === stepIdx ? { ...s, label, key: s.keyEdited ? s.key : slugify(label) } : s,
              ),
            }
          : p,
      ),
    );
  };

  const setStepKey = (phaseIdx: number, stepIdx: number, key: string): void => {
    patchStep(phaseIdx, stepIdx, { key, keyEdited: true });
  };

  const toggleDependsOn = (phaseIdx: number, stepIdx: number, depKey: string): void => {
    setPhases(
      phases.map((p, i) =>
        i === phaseIdx
          ? {
              ...p,
              steps: p.steps.map((s, si) => {
                if (si !== stepIdx) return s;
                const has = s.dependsOn.includes(depKey);
                return {
                  ...s,
                  dependsOn: has
                    ? s.dependsOn.filter((k) => k !== depKey)
                    : [...s.dependsOn, depKey],
                };
              }),
            }
          : p,
      ),
    );
  };

  /** All steps that come strictly before (phaseIdx, stepIdx) in the editor order. */
  const eligibleDeps = (phaseIdx: number, stepIdx: number): { key: string; label: string; phaseLabel: string }[] => {
    const out: { key: string; label: string; phaseLabel: string }[] = [];
    for (let pi = 0; pi <= phaseIdx; pi++) {
      const ph = phases[pi];
      const limit = pi === phaseIdx ? stepIdx : ph.steps.length;
      for (let si = 0; si < limit; si++) {
        const s = ph.steps[si];
        if (!s.key) continue;
        out.push({ key: s.key, label: s.label || s.key, phaseLabel: ph.label || ph.key });
      }
    }
    return out;
  };

  const movePhase = (idx: number, dir: -1 | 1): void => {
    const target = idx + dir;
    if (target < 0 || target >= phases.length) return;
    const next = [...phases];
    [next[idx], next[target]] = [next[target], next[idx]];
    setPhases(next);
  };

  const moveStep = (phaseIdx: number, stepIdx: number, dir: -1 | 1): void => {
    const target = stepIdx + dir;
    const phase = phases[phaseIdx];
    if (target < 0 || target >= phase.steps.length) return;
    const steps = [...phase.steps];
    [steps[stepIdx], steps[target]] = [steps[target], steps[stepIdx]];
    setPhases(phases.map((p, i) => (i === phaseIdx ? { ...p, steps } : p)));
  };

  /** Assemble the editor state into the create/update payload shape. */
  const buildData = () => ({
    name: name.trim(),
    description: description.trim(),
    brandLabel: brandLabel.trim() || undefined,
    brandHeadline: brandHeadline.trim() || undefined,
    brandSubheadline: brandSubheadline.trim() || undefined,
    metadataTabKey: metadataTabKey.trim() || undefined,
    platformOptions: platformOptions
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
    phases: phases.map((p, pi) => ({
      key: p.key || `phase-${pi}`,
      label: p.label || `Phase ${pi + 1}`,
      desc: p.desc,
      order: pi,
      required: p.required,
      steps: p.steps.map((s, si) => ({
        key: s.key || `step-${pi}-${si}`,
        label: s.label || `Step ${si + 1}`,
        desc: s.desc,
        skillRef: s.skillRef,
        order: si,
        dependsOn: s.dependsOn,
        guardrails: s.guardrails,
        confluenceStepSync: s.confluenceStepSync,
        gitStepSync: s.gitStepSync,
        required: s.required,
      })),
    })),
  });

  const handleExport = (): void => {
    const data = buildData();
    downloadJson(assemblyLineFileName(data.name || 'assembly-line'), data);
  };

  /**
   * Persist the assembly line without leaving the editor — used by the per-step
   * "Save guardrails" button so an author can commit a rule change and keep
   * working. Guardrails are part of the line, so this saves the whole thing (as
   * the page-level Save does), then re-hydrates the form from the server.
   */
  const persistStay = async (): Promise<boolean> => {
    if (!name.trim()) {
      setError('Assembly Line name is required');
      return false;
    }
    setSaving(true);
    setError(null);
    try {
      const data = buildData();
      if (editingAssemblyLine) {
        await updateAssemblyLineOnServer(editingAssemblyLine.id, data as any);
        // updateAssemblyLineOnServer clears editingAssemblyLine; reload so the
        // form stays populated and in sync with what was just saved.
        await loadAssemblyLineForEdit(editingAssemblyLine.id);
      } else {
        // A brand-new line has no id yet — create it, then leave for the list
        // (same as the page-level Save for a new line).
        await saveAssemblyLineToServer(data as any);
        navigate('/assembly-lines');
      }
      return true;
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!name.trim()) {
      setError('Assembly Line name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data = buildData();

      if (editingAssemblyLine) {
        await updateAssemblyLineOnServer(editingAssemblyLine.id, data as any);
      } else {
        await saveAssemblyLineToServer(data as any);
      }
      navigate('/assembly-lines');
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const totalSteps = phases.reduce((n, p) => n + p.steps.length, 0);

  return (
    <Shell active="assemblyLines">
      <header style={{ display: 'flex', alignItems: 'center', padding: '14px 28px', borderBottom: '1px solid var(--line)', background: 'var(--surface)', gap: 16, position: 'sticky', top: 0, zIndex: 5 }}>
        <span className="t-h3">Edit Assembly Line</span>
        <span style={{ flex: 1 }} />
        <button className="btn btn--ghost btn--sm" onClick={() => navigate('/assembly-lines')}>Cancel</button>
        <button className="btn btn--sm" onClick={handleExport} title="Download this Assembly Line as JSON">Export</button>
        <button className="btn btn--accent btn--sm" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving...' : 'Save Assembly Line'}
        </button>
      </header>

      <main style={{ padding: '28px 28px 80px', maxWidth: 1080, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
          <span className="t-micro">EDITOR · {isNew ? "NEW ASSEMBLY LINE" : `${editingAssemblyLine?.name?.toUpperCase()} · V${editingAssemblyLine?.version ?? 1}`}</span>
        </div>
        <h1 className="t-h1" style={{ margin: '0 0 4px' }}>{isNew ? 'New Assembly Line' : 'Edit Assembly Line'}</h1>
        <p className="muted" style={{ margin: '0 0 24px' }}>Define the assembly line: which phases run, and which skill powers each step.</p>

        {error && (
          <div style={{ padding: '8px 12px', background: 'var(--err-soft, #ffebe6)', color: 'var(--err)', borderRadius: 4, marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* assemblyLine info section */}
        <section className="card" style={{ marginBottom: 22, padding: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)' }}>
            <Station n="i" size="sm" />
            <span className="t-h3">Assembly Line info</span>
            <span className="t-micro muted">name · description</span>
          </div>
          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Field label="Name">
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Atlassian App Factory" />
            </Field>
            <Field label="Description">
              <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What does this Assembly Line do?" />
            </Field>
          </div>
        </section>

        {/* phases section */}
        <section className="card" style={{ padding: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)' }}>
            <Station n={phases.length} size="sm" />
            <span className="t-h3">Phases</span>
            <span className="t-micro muted">{phases.length} phases · {totalSteps} steps</span>
            <span style={{ flex: 1 }} />
            <button className="btn btn--sm" onClick={addPhase}>{I.plus} Add phase</button>
          </div>

          <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {phases.map((phase, pi) => (
              <div key={pi} className="card" style={{ padding: 0, borderColor: 'var(--line)', background: 'var(--bg)' }}>
                {/* phase header — label first, then auto-derived key, then description */}
                <div style={{ display: 'grid', gridTemplateColumns: '32px 200px 160px 1fr auto', gap: 10, padding: '12px 14px', alignItems: 'end', borderBottom: '1px solid var(--line)', background: 'var(--surface-2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', height: 28 }}>
                    <Station n={pi + 1} />
                  </div>
                  <LabelledField label="Phase label">
                    <input
                      className="input"
                      value={phase.label}
                      onChange={(e) => setPhaseLabel(pi, e.target.value)}
                      placeholder="Discovery"
                      style={{ height: 28, fontWeight: 600 }}
                    />
                  </LabelledField>
                  <LabelledField label="Key" hint="auto">
                    <input
                      className="input mono"
                      value={phase.key}
                      onChange={(e) => setPhaseKey(pi, e.target.value)}
                      placeholder="discovery"
                      style={{ height: 28 }}
                    />
                  </LabelledField>
                  <LabelledField label="Description">
                    <input
                      className="input"
                      value={phase.desc}
                      onChange={(e) => updatePhaseDesc(pi, e.target.value)}
                      placeholder="Phase description"
                      style={{ height: 28 }}
                    />
                  </LabelledField>
                  <div style={{ display: 'flex', gap: 4, paddingBottom: 1, alignItems: 'center' }}>
                    <button
                      type="button"
                      className={`sync-chip${phase.required ? ' sync-chip--on' : ''}`}
                      onClick={() => togglePhaseRequired(pi)}
                      title="Required phases can't be skipped in the run's Advanced settings"
                    >
                      {I.check}
                      Required
                    </button>
                    <button className="btn btn--ghost btn--sm btn--icon" onClick={() => movePhase(pi, -1)} disabled={pi === 0}>{I.chevU}</button>
                    <button className="btn btn--ghost btn--sm btn--icon" onClick={() => movePhase(pi, 1)} disabled={pi === phases.length - 1}>{I.chevD}</button>
                    <button className="btn btn--ghost btn--sm btn--icon" style={{ color: 'var(--err)' }} onClick={() => removePhase(pi)}>{I.trash}</button>
                  </div>
                </div>

                {/* steps */}
                <div style={{ padding: '10px 14px 12px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {phase.steps.map((step, si) => {
                    const candidates = eligibleDeps(pi, si);
                    return (
                      <div key={si} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--surface)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr 160px 220px auto', gap: 10, alignItems: 'end' }}>
                          <span className="t-num t-small muted" style={{ paddingLeft: 4, paddingBottom: 6 }}>{pi + 1}.{si + 1}</span>
                          <LabelledField label="Step label">
                            <input
                              className="input"
                              value={step.label}
                              onChange={(e) => setStepLabel(pi, si, e.target.value)}
                              placeholder="Generate spec"
                              style={{ height: 28 }}
                            />
                          </LabelledField>
                          <LabelledField label="Key" hint="auto">
                            <input
                              className="input mono"
                              value={step.key}
                              onChange={(e) => setStepKey(pi, si, e.target.value)}
                              placeholder="generate-spec"
                              style={{ height: 28 }}
                            />
                          </LabelledField>
                          <LabelledField label="Skill">
                            <SkillPicker
                              value={step.skillRef}
                              skills={skills}
                              onChange={(v) => patchStep(pi, si, { skillRef: v })}
                            />
                          </LabelledField>
                          <div style={{ display: 'flex', gap: 4, paddingBottom: 1 }}>
                            <button className="btn btn--ghost btn--sm btn--icon" onClick={() => moveStep(pi, si, -1)} disabled={si === 0}>{I.chevU}</button>
                            <button className="btn btn--ghost btn--sm btn--icon" onClick={() => moveStep(pi, si, 1)} disabled={si === phase.steps.length - 1}>{I.chevD}</button>
                            <button className="btn btn--ghost btn--sm btn--icon" style={{ color: 'var(--err)' }} onClick={() => removeStep(pi, si)}>{I.x}</button>
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr', gap: 10, alignItems: 'center' }}>
                          <span className="t-micro muted">deps</span>
                          {candidates.length === 0 ? (
                            <span className="t-small muted">No earlier steps to depend on.</span>
                          ) : (
                            <div style={{ maxWidth: 380 }}>
                              <DepsPicker
                                candidates={candidates}
                                selected={step.dependsOn}
                                onToggle={(key) => toggleDependsOn(pi, si, key)}
                              />
                            </div>
                          )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr', gap: 10, alignItems: 'center' }}>
                          <span className="t-micro muted">sync</span>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className={`sync-chip${step.confluenceStepSync ? ' sync-chip--on' : ''}`}
                              onClick={() => patchStep(pi, si, { confluenceStepSync: !step.confluenceStepSync })}
                              title="Show the Confluence target picker + “Sync step” button for this step in built apps"
                            >
                              {I.confluence}
                              Confluence
                            </button>
                            <button
                              type="button"
                              className={`sync-chip${step.gitStepSync ? ' sync-chip--on' : ''}`}
                              onClick={() => patchStep(pi, si, { gitStepSync: !step.gitStepSync })}
                              title="Include this step’s artifacts in git host sync (push/pull) for built apps"
                            >
                              {I.git}
                              Git
                            </button>
                            <button
                              type="button"
                              className={`sync-chip${step.required ? ' sync-chip--on' : ''}`}
                              onClick={() => patchStep(pi, si, { required: !step.required })}
                              title="Required steps can't be skipped in the run's Advanced settings"
                            >
                              {I.check}
                              Required
                            </button>
                            {phase.required && !step.required && (
                              <span className="t-small muted">Phase is required — all steps run.</span>
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr', gap: 10, alignItems: 'start' }}>
                          <span className="t-micro muted" style={{ paddingTop: 6 }}>guards</span>
                          <StepGuardrailsField
                            guardrails={step.guardrails}
                            onChange={(next) => patchStep(pi, si, { guardrails: next })}
                            onSave={persistStay}
                            saving={saving}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <button
                    className="btn btn--ghost"
                    onClick={() => addStep(pi)}
                    style={{
                      height: 30,
                      width: '100%',
                      border: '1px dashed var(--line-strong)',
                      color: 'var(--ink-muted)',
                      borderRadius: 5,
                      justifyContent: 'center',
                      marginTop: 4,
                      background: 'transparent',
                    }}
                  >
                    {I.plus} Add step
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="perf-edge" style={{ marginTop: 24 }} />
        <div style={{ display: 'flex', gap: 18, marginTop: 14, alignItems: 'center' }}>
          <span className="t-micro muted">{saving ? 'SAVING...' : 'UNSAVED'} · {phases.length} phases · {totalSteps} steps</span>
          <span style={{ flex: 1 }} />
          <button className="btn btn--ghost btn--sm" onClick={() => navigate('/assembly-lines')}>Discard</button>
          <button className="btn btn--accent btn--sm" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving...' : 'Save Assembly Line'}
          </button>
        </div>
      </main>
    </Shell>
  );
}

/** Close `onClose` when a mousedown lands outside `ref`, while `active`. */
function useClickOutside(ref: React.RefObject<HTMLElement>, onClose: () => void, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [ref, onClose, active]);
}

/** Single-select, searchable skill dropdown. */
function SkillPicker({ value, skills, onChange }: { value: string; skills: SkillInfo[]; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? skills.filter((s) => `${s.name} ${s.description}`.toLowerCase().includes(needle))
    : skills;

  const close = (): void => {
    setOpen(false);
    setQ('');
  };

  return (
    <div className="dd" ref={ref}>
      <button
        type="button"
        className={`dd-trigger mono${open ? ' dd-trigger--open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`dd-trigger-label${value ? '' : ' dd-placeholder'}`}>{value || 'Select skill'}</span>
        <span className={`dd-caret${open ? ' dd-caret--open' : ''}`}>{I.chevD}</span>
      </button>
      {open && (
        <div className="dd-menu" role="listbox">
          <div className="dd-search">
            {I.search}
            <input
              autoFocus
              className="dd-search-input"
              placeholder="Search skills..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="dd-list">
            {filtered.length === 0 ? (
              <div className="dd-empty">No skills match.</div>
            ) : (
              filtered.map((s) => {
                const active = s.name === value;
                return (
                  <button
                    key={s.name}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`dd-item${active ? ' dd-item--active' : ''}${s.available === false ? ' dd-item--muted' : ''}`}
                    onClick={() => {
                      onChange(s.name);
                      close();
                    }}
                  >
                    <span className="dd-radio">{active ? I.check : null}</span>
                    <span className="dd-item-text">
                      <span className="dd-item-label mono">
                        {s.name}
                        {s.available === false ? ' · unavailable' : ''}
                      </span>
                      {s.description ? <span className="dd-item-desc">{s.description}</span> : null}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Multi-select, searchable dependency dropdown with checkboxes. */
function DepsPicker({
  candidates,
  selected,
  onToggle,
}: {
  candidates: { key: string; label: string; phaseLabel: string }[];
  selected: string[];
  onToggle: (key: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? candidates.filter((c) => `${c.label} ${c.key} ${c.phaseLabel}`.toLowerCase().includes(needle))
    : candidates;

  const count = selected.length;
  const summary = count === 0 ? 'No dependencies' : `${count} ${count === 1 ? 'dependency' : 'dependencies'} selected`;

  return (
    <div className="dd" ref={ref}>
      <button
        type="button"
        className={`dd-trigger${open ? ' dd-trigger--open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`dd-trigger-label${count ? '' : ' dd-placeholder'}`}>{summary}</span>
        <span className={`dd-caret${open ? ' dd-caret--open' : ''}`}>{I.chevD}</span>
      </button>
      {open && (
        <div className="dd-menu" role="listbox" aria-multiselectable="true">
          <div className="dd-search">
            {I.search}
            <input
              autoFocus
              className="dd-search-input"
              placeholder="Search steps..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="dd-list">
            {filtered.length === 0 ? (
              <div className="dd-empty">No steps match.</div>
            ) : (
              filtered.map((c) => {
                const active = selected.includes(c.key);
                return (
                  <button
                    key={c.key}
                    type="button"
                    role="option"
                    aria-selected={active}
                    className={`dd-item${active ? ' dd-item--active' : ''}`}
                    onClick={() => onToggle(c.key)}
                    title={`${c.phaseLabel} · ${c.key}`}
                  >
                    <span className={`dd-check${active ? ' dd-check--on' : ''}`}>{active ? I.check : null}</span>
                    <span className="dd-item-text">
                      <span className="dd-item-label">{c.label}</span>
                    </span>
                    <span className="dd-item-hint mono">{c.phaseLabel}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible guardrails field for a step. Collapsed it shows a summary + an
 * "Add guardrails" / "Edit" button; opened it reveals the single-textarea editor
 * with Clear + Save + Cancel. Editing is live on the step form; Save persists
 * the assembly line and, on success, collapses the box. Cancel reverts to the
 * snapshot taken when the box was opened.
 */
function StepGuardrailsField({
  guardrails,
  onChange,
  onSave,
  saving,
}: {
  guardrails: Guardrail[];
  onChange: (next: Guardrail[]) => void;
  onSave: () => Promise<boolean>;
  saving: boolean;
}) {
  const [open, setOpen] = useState(false);
  const snapshot = useRef<Guardrail[]>(guardrails);

  const openEditor = (): void => {
    snapshot.current = guardrails;
    setOpen(true);
  };
  const cancel = (): void => {
    onChange(snapshot.current);
    setOpen(false);
  };
  const save = async (): Promise<void> => {
    const ok = await onSave();
    if (ok) setOpen(false);
  };

  if (!open) {
    const count = guardrails.length;
    if (count === 0) {
      return (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={openEditor}
          style={{
            height: 30,
            width: '100%',
            border: '1px dashed var(--line-strong)',
            color: 'var(--ink-muted)',
            borderRadius: 5,
            justifyContent: 'center',
          }}
        >
          {I.plus} Add guardrails
        </button>
      );
    }
    const warnCount = guardrails.filter((g) => g.enforcement === 'warn').length;
    const offCount = guardrails.filter((g) => g.enforcement === 'off').length;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="t-small muted">
          {count} guardrail{count !== 1 ? 's' : ''}
          {warnCount ? ` · ${warnCount} warn` : ''}
          {offCount ? ` · ${offCount} off` : ''}
        </span>
        <button type="button" className="btn btn--ghost btn--sm" onClick={openEditor}>
          Edit
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <GuardrailsEditor
        guardrails={guardrails}
        onChange={onChange}
        onSave={() => void save()}
        saving={saving}
        saveLabel="Save guardrails"
      />
      <div>
        <button type="button" className="btn btn--ghost btn--sm" onClick={cancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, children, full }: { label: string; hint?: string; children: React.ReactNode; full?: boolean }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
        <span className="label" style={{ margin: 0 }}>{label}</span>
        {hint && <span className="t-small muted">-- {hint}</span>}
      </div>
      {children}
    </div>
  );
}

/** Compact inline field label used inside the phase/step grid rows. */
function LabelledField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3 }}>
        <span className="t-micro muted" style={{ letterSpacing: '0.05em' }}>{label}</span>
        {hint && <span className="t-micro muted" style={{ opacity: 0.7 }}>· {hint}</span>}
      </div>
      {children}
    </div>
  );
}
